<script setup lang="ts">
import {
  CaseTransformer,
  isNil,
  keysChangeCase,
  toBoolean,
} from '@revolugo/common/utils'
import { RProgressSpinner } from '@revolugo/design-system/atoms'
import {
  BookingState,
  Event,
  RevolugoVueFullBookingFlow,
  RevolugoVueHotelOffersView,
} from '@revolugo/elements-vue3'
import { objectPick, useElementSize } from '@vueuse/core'
import { omitBy } from 'lodash-es'

import type { HotelOfferRequestCreateApi } from '@revolugo/booking-api-client'

definePageMeta({
  layout: 'full-page',
})

const { t } = useI18n()

useHead({
  title: t('pageTitle'),
})

const { elementsLoaded, eventBus } = useEventBus()
const router = useRouter()
const route = useRoute()
const localePath = useLocalePath()

const sanitizedQueryParams = ref(
  keysChangeCase(route.query, CaseTransformer.Camel),
)

const { latitude: geoLocationLatitude, longitude: geoLocationLongitude } =
  useGeolocation()

const { elementsRequest, event, request, settings, venue } = await useSettings(
  sanitizedQueryParams.value,
)

const { prebookProps, updatePrebookQuery } = usePrebookQuery()

const isFullBookingFlow = ref(sanitizedQueryParams.value.mode === 'full')

const latitude = computed(() => {
  if (request.value?.latitude) {
    return request.value.latitude
  }

  return geoLocationLatitude.value
})
const longitude = computed(() => {
  if (request.value?.longitude) {
    return request.value.longitude
  }

  return geoLocationLongitude.value
})

if (event.value?.name) {
  useHead({
    title: t('pageTitleEvent', { name: event.value.name }),
  })
}

const onHotelOfferSelected = (
  hotelId: string,
  payload: HotelOfferRequestCreateApi,
) => {
  const routeData = router.resolve({
    path: localePath(`/hotels/${hotelId}`),
  })

  const querySearchParams = new URLSearchParams(
    omitBy(
      {
        ...sanitizedQueryParams.value,
        ...objectPick(payload, HOTEL_OFFER_REQUEST_QUERY_KEYS),
      },
      isNil,
    ) as Record<string, string>,
  )
  window.open(`${routeData.href}?${querySearchParams.toString()}`, '_blank')
}

const screenElementRef = ref<HTMLElement | null>(null)
const { height: screenSize } = useElementSize(screenElementRef)
const step = ref<BookingState>(BookingState.Idle)

const hotelOffersViewHeight = computed(() => {
  if (step.value === BookingState.Idle) {
    return screenSize.value
  }

  return undefined
})

useCaptureEvents(eventBus)

onMounted(() => {
  eventBus.value.$on(Event.HotelOffersItemClick, payload => {
    if (!isFullBookingFlow.value) {
      onHotelOfferSelected(payload.id, elementsRequest.value)
    }
  })

  eventBus.value.$on(
    Event.HotelOfferRequestCreated,
    (payload: HotelOfferRequestCreateApi) => {
      request.value = payload

      router.replace({
        query: omitBy(
          {
            ...sanitizedQueryParams.value,
            ...objectPick(payload, HOTEL_OFFER_REQUEST_QUERY_KEYS),
          },
          value => value === null || value === undefined,
        ) as Record<string, string>,
      })
    },
  )

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  eventBus.value.$on(Event.PrebookForm, updatePrebookQuery)

  eventBus.value.$on(
    Event.BookingFlowStepUpdated,
    value => (step.value = value),
  )
})
</script>

<i18n lang="json" src="./hotels.i18n.json" />

<template>
  <div
    ref="screenElementRef"
    class="fixed top-4 left-4 bottom-4 right-4"
  ></div>
  <div class="m-0 p-4 h-full">
    <div class="h-full">
      <div>
        <NuxtPage v-if="route.params.id" />
        <div
          v-else-if="latitude && longitude"
          ref="hotelOffersView"
          class="h-full"
        >
          <div
            v-if="!elementsLoaded"
            class="flex justify-center items-center h-full w-full"
          >
            <RProgressSpinner />
          </div>
          <RevolugoVueFullBookingFlow
            v-if="isFullBookingFlow"
            class="block"
            v-bind="{
              ...elementsRequest,
              ...prebookProps,
            }"
            :features="{
              HotelOffersView: {
                HotelOffersMap: {
                  SearchAreaButton: true,
                  LocationInput: !settings?.hideAddressAutocompleteInput,
                },
                PreferencesMenu: true,
              },
            }"
            :height="
              hotelOffersViewHeight ? `${hotelOffersViewHeight}px` : undefined
            "
            :latitude="latitude"
            :limit="100"
            :longitude="longitude"
            :map-first-on-mobile="
              toBoolean(sanitizedQueryParams.mapFirstOnMobile)
            "
            :markers="
              settings?.mapMarkers?.map(marker => ({
                ...marker,
                tooltip: marker.name,
                imgUrl: marker.markerPicture,
              }))
            "
            :venue="venue"
          ></RevolugoVueFullBookingFlow>
          <RevolugoVueHotelOffersView
            v-else
            class="block"
            v-bind="{
              ...elementsRequest,
              ...prebookProps,
            }"
            :features="{
              HotelOffersMap: {
                SearchAreaButton: true,
                LocationInput: !settings?.hideAddressAutocompleteInput,
              },
              PreferencesMenu: true,
            }"
            :height="
              hotelOffersViewHeight ? `${hotelOffersViewHeight}px` : undefined
            "
            :latitude="latitude"
            :limit="100"
            :longitude="longitude"
            :map-first-on-mobile="
              toBoolean(sanitizedQueryParams.mapFirstOnMobile)
            "
            :markers="
              settings?.mapMarkers?.map(marker => ({
                ...marker,
                tooltip: marker.name,
                imgUrl: marker.markerPicture,
              }))
            "
            :venue="venue"
          />
        </div>
      </div>
    </div>
  </div>
</template>
